import { useCallback, useMemo, useState } from 'react';
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  GET_ACTIVE_FORECAST_SCENARIO, GET_ALL_ATTRIBUTION_SETTINGS,
  GET_ALL_MULTI_RISK_SENSITIVITY_SCENARIOS, GET_ALL_MULTI_RISK_STRESS_SCENARIOS,
  GET_ALL_RISK_SENSITIVITY_SCENARIOS, GET_ALL_RISK_SETTINGS,
  GET_ALL_RISK_SIMULATION_CONFIGURATIONS, GET_ALL_RISK_STRESS_SCENARIOS,
  GET_FORECAST_ASSUMPTIONS,
  GET_FORECAST_SCENARIOS, GET_TABLE_TRANSLATIONS, GET_TRANSLATION_CORRECTION_PROGRESS, GET_TRIGGERED_EVENTS,
  GET_TRANSLATION_TABLES, GET_UI_LABELS,
  GET_INVESTOR_QA_ENTRIES, GET_MF_SCHEMES_MAPPING, GET_USER_DEFINED_TAGS,
  VR_BULK_RUN_IDS, VR_BULK_TABLES,
} from '../queries/config';
/* Shared with the analytics screens rather than re-declared: one document per
   operation name, or a refetch of one fires the other. */
import { GET_BENCHMARKS, GET_SECURITY_GROUPS } from '../queries/attribution';
import {
  BACKFILL_UI_LABEL_AGENT_PROMPTS, BUILD_UNIVERSE_MEAN_COVARIANCE,
  CALCULATE_FACTOR_PORTFOLIO_RETURNS, CLONE_RISK_SENSITIVITY_SCENARIO, CLONE_RISK_SETTINGS,
  CLONE_RISK_SIMULATION_CONFIGURATION, CLONE_RISK_STRESS_SCENARIO, CLONE_SECURITY_GROUP,
  COMPUTE_ASSET_MODEL_EXPOSURES, DELETE_ATTRIBUTION_SETTINGS, DELETE_BENCHMARK,
  DELETE_FORECAST_ASSUMPTION, DELETE_RISK_SENSITIVITY_SCENARIO, DELETE_RISK_SETTINGS,
  DELETE_RISK_SIMULATION_CONFIGURATION, DELETE_RISK_STRESS_SCENARIO, DELETE_SECURITY_GROUP,
  DELETE_USER_DEFINED_TAG, LOAD_ASSET_MODELS_FROM_FILE, LOAD_GOAL_PLANNING_DATA,
  RECOMPUTE_GOAL_DATA, REFRESH_SIMULATION_GRID, REGENERATE_UI_LABEL_AGENT_PROMPT, RUN_TRANSLATION_CORRECTION,
  SYNC_ALL_ASSET_MODELS, SYNC_VR_MUTUAL_FUNDS_INCREMENTAL, TRIGGER_PARAMETRIC_RISK_SIMULATION,
  UPDATE_COUNTRY_STOCKS, UPDATE_EXCHANGE_COMPLETE, UPDATE_EXCHANGE_STOCKS,
  UPDATE_FACTOR_INDEX_EXPOSURES, UPDATE_INDIAN_MARKET_COMPLETE, UPDATE_INDIAN_MARKET_DAILY,
  UPDATE_MF_EXPOSURES, UPDATE_SECURITY_SEARCH_INDEX, SYNC_PHYSICAL_GOLD_HISTORICAL_PRICES, LOAD_VR_TABLE_FROM_S3,
  SEED_CUSTOMER_FAQS, REFRESH_COMPARATIVE_EXPLAINER,
  UPDATE_STOCK_EXPOSURES, UPDATE_TABLE_TRANSLATIONS, UPDATE_VOL_ADJUSTED_EXPOSURES,
} from '../mutations/config';

/**
 * The Configuration tabs' data layer.
 *
 * Two shapes, and the split is the point:
 *
 *   - a CONFIGURATION area is read, edited and removed from — the risk
 *     scenarios, forecasts, benchmarks, groups, tags, labels;
 *   - an OPERATION is fired and reported on — the bootstrap loaders, the data
 *     loads, the recalculations. These are declarative lists rather than a
 *     component each, so "run everything" and the individual buttons can never
 *     disagree about what everything is.
 *
 * Names are prefixed `useConfig…` wherever the app already has a hook of the
 * obvious name reading the same data for a screen: the advisor's `useBenchmarks`
 * is a picker, this one is the register.
 */

const READ = { fetchPolicy: 'cache-and-network', errorPolicy: 'all' };

/** A list read, reduced to what every tab needs from it. */
function useList(doc, pick, options = {}) {
  const { data, loading, error, refetch } = useQuery(doc, { ...READ, ...options });
  return useMemo(() => ({
    rows: pick(data) ?? [],
    loading,
    error,
    refetch,
  }), [data, loading, error, refetch, pick]);
}

/* ── risk ───────────────────────────────────────────────────────────────── */

const pickRiskSettings = (d) => d?.getAllRiskSettings;
const pickStress = (d) => d?.getAllRiskStressScenarios;
const pickSensitivity = (d) => d?.getAllRiskSensitivityScenarios;
const pickMultiStress = (d) => d?.getAllMultiRiskStressScenarios;
const pickMultiSensitivity = (d) => d?.getAllMultiRiskSensitivityScenarios;
const pickSimConfigs = (d) => d?.getAllRiskSimulationConfigurations;

export const useConfigRiskSettings = () => useList(GET_ALL_RISK_SETTINGS, pickRiskSettings);
export const useRiskStressScenarios = () => useList(GET_ALL_RISK_STRESS_SCENARIOS, pickStress);
export const useRiskSensitivityScenarios = () => useList(GET_ALL_RISK_SENSITIVITY_SCENARIOS, pickSensitivity);
export const useMultiRiskStressScenarios = () => useList(GET_ALL_MULTI_RISK_STRESS_SCENARIOS, pickMultiStress);
export const useMultiRiskSensitivityScenarios = () => useList(GET_ALL_MULTI_RISK_SENSITIVITY_SCENARIOS, pickMultiSensitivity);
export const useRiskSimConfigs = (userTag = 'default') => useList(
  GET_ALL_RISK_SIMULATION_CONFIGURATIONS, pickSimConfigs, { variables: { userTag } },
);

const riskRefresh = (name) => ({ refetchQueries: [name] });
export const useDeleteRiskSettings = () => useMutation(DELETE_RISK_SETTINGS, riskRefresh('GetAllRiskSettings'));
export const useCloneRiskSettings = () => useMutation(CLONE_RISK_SETTINGS, riskRefresh('GetAllRiskSettings'));
export const useDeleteStressScenario = () => useMutation(DELETE_RISK_STRESS_SCENARIO, riskRefresh('GetAllRiskStressScenarios'));
export const useCloneStressScenario = () => useMutation(CLONE_RISK_STRESS_SCENARIO, riskRefresh('GetAllRiskStressScenarios'));
export const useDeleteSensitivityScenario = () => useMutation(DELETE_RISK_SENSITIVITY_SCENARIO, riskRefresh('GetAllRiskSensitivityScenarios'));
export const useCloneSensitivityScenario = () => useMutation(CLONE_RISK_SENSITIVITY_SCENARIO, riskRefresh('GetAllRiskSensitivityScenarios'));
export const useDeleteSimConfig = () => useMutation(DELETE_RISK_SIMULATION_CONFIGURATION, riskRefresh('GetAllRiskSimulationConfigurations'));
export const useCloneSimConfig = () => useMutation(CLONE_RISK_SIMULATION_CONFIGURATION, riskRefresh('GetAllRiskSimulationConfigurations'));

/* ── attribution, forecasts, benchmarks, universes, tags ────────────────── */

export const useConfigAttributionSettings = () => useList(
  GET_ALL_ATTRIBUTION_SETTINGS, (d) => d?.getAllAttributionSettings, { variables: { skip: 0, limit: 200 } },
);
export const useDeleteAttributionSettings = () => useMutation(DELETE_ATTRIBUTION_SETTINGS, {
  refetchQueries: ['GetAllAttributionSettings'],
});

export const useForecastScenarios = () => useList(
  GET_FORECAST_SCENARIOS, (d) => d?.getForecastScenarios, { variables: { skip: 0, limit: 100 } },
);
export function useActiveForecastScenario() {
  const { data, loading, error } = useQuery(GET_ACTIVE_FORECAST_SCENARIO, READ);
  return { scenario: data?.getActiveForecastScenario ?? null, loading, error };
}
export const useForecastAssumptions = (scenarioId) => useList(
  GET_FORECAST_ASSUMPTIONS, (d) => d?.getForecastAssumptions,
  { variables: { scenarioId: Number(scenarioId) }, skip: scenarioId == null },
);
export const useDeleteForecastAssumption = () => useMutation(DELETE_FORECAST_ASSUMPTION, {
  refetchQueries: ['GetForecastAssumptions'],
});

/* The register's benchmarks, active or inactive. The backend requires
   isActive (Boolean!), so the register picks one side at a time. */
export const useConfigBenchmarks = (isActive = true) => useList(
  GET_BENCHMARKS, (d) => d?.getBenchmarks, { variables: { securityGroupId: null, isActive: Boolean(isActive) } },
);
export const useDeleteBenchmark = () => useMutation(DELETE_BENCHMARK, { refetchQueries: ['GetBenchmarks'] });

/* Likewise: the picker drops inactive groups, a register must not. */
export const useConfigSecurityGroups = () => useList(GET_SECURITY_GROUPS, (d) => d?.getSecurityGroups);
export const useCloneSecurityGroup = () => useMutation(CLONE_SECURITY_GROUP, { refetchQueries: ['GetSecurityGroups'] });
export const useDeleteSecurityGroup = () => useMutation(DELETE_SECURITY_GROUP, { refetchQueries: ['GetSecurityGroups'] });

export const useUserDefinedTags = () => useList(GET_USER_DEFINED_TAGS, (d) => d?.getUserDefinedTags);
export const useDeleteUserDefinedTag = () => useMutation(DELETE_USER_DEFINED_TAG, {
  refetchQueries: ['GetUserDefinedTags'],
});

/* ── labels and translations ────────────────────────────────────────────── */

export const useUiLabels = (labelGroup = null, languageCode = null) => useList(
  GET_UI_LABELS, (d) => d?.getUiLabels, { variables: { labelGroup, languageCode } },
);
export const useBackfillLabelPrompts = () => useMutation(BACKFILL_UI_LABEL_AGENT_PROMPTS, {
  refetchQueries: ['GetUiLabels'],
});
export const useRegenerateLabelPrompt = () => useMutation(REGENERATE_UI_LABEL_AGENT_PROMPT, {
  refetchQueries: ['GetUiLabels'],
});

const NO_ITEMS = [];

/* The readable tables, each `{ key, displayName }`. A failed read
   (success: false) is surfaced as an error rather than an empty list. */
export function useTranslationTables() {
  const { data, loading, error, refetch } = useQuery(GET_TRANSLATION_TABLES, READ);
  return useMemo(() => {
    const res = data?.getTranslationTables;
    const failed = Boolean(res && res.success === false);
    return {
      rows: failed ? NO_ITEMS : (res?.tables ?? NO_ITEMS).filter((t) => t?.key),
      loading,
      error: error ?? (failed ? new Error(res.message || 'Could not load the tables') : null),
      refetch,
    };
  }, [data, loading, error, refetch]);
}

/** Records per page — every read of a table asks for this many at a time. */
export const TRANSLATION_PAGE_SIZE = 100;
const FIRST_PAGE = [null];

/* One row per field of a translated record — the unit a translator reads and
   corrects. */
const flattenTranslation = (item) => (item?.fields ?? NO_ITEMS).map((f) => ({
  key: `${item.id}-${f.fieldName}`,
  translationId: item.id,
  baseId: item.baseId,
  languageCode: item.languageCode,
  isHumanVerified: item.isHumanVerified,
  fieldName: f.fieldName,
  originalValue: f.originalValue,
  translatedValue: f.translatedValue,
}));

/**
 * One table's translations, a page at a time. `table` is the table's `key`.
 *
 * Nothing is read until a table is named. Each page is its own call to
 * `getTableTranslations` with `limit: TRANSLATION_PAGE_SIZE`; `nextPage` asks
 * again with `after` = the page's `nextCursor`, and the cursors already used
 * are kept so `prevPage` steps back to a page Apollo already holds. Switching
 * table or language starts again at page 1.
 *
 * Saves go through `updateTableTranslations`, then the page on screen is read
 * again so it shows what was stored.
 */
export function useTableTranslations(table, languageCode = null) {
  const scope = `${table}|${languageCode ?? ''}`;
  const [paging, setPaging] = useState({ scope, cursors: FIRST_PAGE });
  const cursors = paging.scope === scope ? paging.cursors : FIRST_PAGE;
  const after = cursors[cursors.length - 1];

  const { data, loading, error, refetch } = useQuery(GET_TABLE_TRANSLATIONS, {
    ...READ,
    variables: { table, languageCode, limit: TRANSLATION_PAGE_SIZE, after },
    skip: !table,
  });

  const page = table ? data?.getTableTranslations : null;
  const pageError = page && page.success === false
    ? new Error(page.message || 'Could not load translations')
    : null;
  const items = page && page.success !== false ? page.items ?? NO_ITEMS : NO_ITEMS;
  const rows = useMemo(() => items.flatMap(flattenTranslation), [items]);

  const hasNext = Boolean(page?.hasMore && page?.nextCursor);
  const nextPage = useCallback(() => {
    if (hasNext) setPaging({ scope, cursors: [...cursors, page.nextCursor] });
  }, [hasNext, scope, cursors, page]);
  const prevPage = useCallback(() => {
    if (cursors.length > 1) setPaging({ scope, cursors: cursors.slice(0, -1) });
  }, [scope, cursors]);

  const [mutate, saveState] = useMutation(UPDATE_TABLE_TRANSLATIONS, {
    refetchQueries: ['GetTableTranslations'],
  });

  /** Save edits; resolves with the saved records, throws the first failure. */
  const save = useCallback(async (inputs) => {
    const { data: saved } = await mutate({ variables: { table, inputs } });
    const res = saved?.updateTableTranslations;
    const results = res?.results ?? NO_ITEMS;
    const failed = results.find((r) => r && r.success === false);
    if (failed) throw new Error(failed.message || 'Could not save this translation');
    if (res?.success === false) throw new Error(res.message || 'Could not save the translations');
    return results.map((r) => r?.updatedTranslation).filter(Boolean);
  }, [mutate, table]);

  return {
    rows,
    records: items.length,
    page: cursors.length,
    hasNext,
    hasPrev: cursors.length > 1,
    nextPage,
    prevPage,
    loading,
    error: error ?? pageError,
    refetch,
    save,
    saving: saveState.loading,
  };
}

/* ── translation correction ─────────────────────────────────────────────── */

/* A sweep still under way, by its state — the backend's words for it are not
   enumerated, so anything that reads as queued or running counts, and anything
   finished, failed or cancelled does not. */
const SWEEP_ACTIVE = /queue|pend|run|start|progress|process|enqueue|schedul|active/i;
const SWEEP_OVER = /done|complete|finish|succe|fail|error|cancel|abort|stop/i;
export const isSweepActive = (state) => Boolean(state) && SWEEP_ACTIVE.test(state) && !SWEEP_OVER.test(state);

/* A triggered event that is a translation correction. */
const isCorrectionEvent = (e) => /translat|correct/i.test(`${e.kind} ${e.eventType} ${e.source ?? ''}`);

/**
 * Start a correction sweep: `run({ tables, languages })`. Tables are keys;
 * none means the cache sweep. Resolves with the queued runs and throws when the
 * backend refuses (`success: false`). The run list is re-read; progress is not.
 */
export function useRunTranslationCorrection() {
  const [mutate, state] = useMutation(RUN_TRANSLATION_CORRECTION, {
    refetchQueries: ['GetTriggeredEvents'],
  });
  const run = useCallback(async ({ tables = null, languages = null } = {}) => {
    const { data } = await mutate({
      variables: {
        tables: tables?.length ? tables : null,
        languages: languages?.length ? languages : null,
      },
    });
    const res = data?.runTranslationCorrection;
    if (!res?.success) throw new Error(res?.message || 'The correction could not be started');
    return res;
  }, [mutate]);
  return { run, running: state.loading };
}

/**
 * One sweep's progress — a table's by key, or the cache sweep's with no table —
 * read only when asked: `check()` reads it once, fresh. Nothing polls.
 */
export function useTranslationCorrectionProgress(table) {
  const [load, { data, loading, error, called }] = useLazyQuery(GET_TRANSLATION_CORRECTION_PROGRESS, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const check = useCallback(() => load({ variables: { table: table ?? null } }), [load, table]);
  const res = data?.getTranslationCorrectionProgress ?? null;
  /* A reading for another table (the one open before) is not shown. */
  const forThis = res && (res.table ?? null) === (table ?? null) ? res : res && res.success === false ? res : null;
  return {
    check,
    checked: called,
    progress: forThis && forThis.success !== false ? forThis : null,
    refused: forThis && forThis.success === false ? (forThis.message || 'Not available') : null,
    loading,
    error,
  };
}

/**
 * The correction runs started for one subject — a table key, or with `null`
 * the cache sweep's — newest first, read once when asked for (and again after
 * a run is started). `running` says whether any of them is still under way:
 * that is what shows the Progress button.
 */
export function useTriggeredEvents(subject, { skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(GET_TRIGGERED_EVENTS, {
    variables: { subject: subject ?? null }, skip, fetchPolicy: 'network-only', errorPolicy: 'all',
  });
  return useMemo(() => {
    const all = (data?.getTriggeredEvents ?? NO_ITEMS).filter(isCorrectionEvent);
    /* With no subject the list is every run; the cache sweep's are those
       naming no table (or the cache itself). */
    const rows = (subject ? all : all.filter((e) => !e.subject || /cache/i.test(e.subject)))
      .slice()
      .sort((a, b) => String(b.enqueuedAt ?? '').localeCompare(String(a.enqueuedAt ?? '')));
    return {
      rows,
      running: rows.some((e) => !e.cancelled && isSweepActive(e.state)),
      loading,
      error,
      refetch,
    };
  }, [data, subject, loading, error, refetch]);
}

/* ── investor Q&A knowledge base ────────────────────────────────────────── */

/** How many rows a page of the knowledge base holds. */
export const QA_PAGE_SIZE = 25;

/**
 * The Q&A pairs the assistant answers investors from.
 *
 * Paged on the server, and BOTH filters are the server's too: `archetype`
 * matches the tag, `question` runs a semantic search over the embeddings. A
 * new question is a new candidate set, not a reordering — so narrowing the
 * current page locally would silently demote a semantic search to a substring
 * match over 25 rows.
 */
export function useInvestorQa({ page = 0, archetype = '', question = '' } = {}) {
  const { data, loading, error, refetch, networkStatus } = useQuery(GET_INVESTOR_QA_ENTRIES, {
    variables: {
      skip: page * QA_PAGE_SIZE,
      limit: QA_PAGE_SIZE,
      archetype: archetype.trim() || null,
      question: question.trim() || null,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const payload = data?.getInvestorQaEntries;
    const total = payload?.total ?? 0;
    return {
      rows: payload?.entries ?? [],
      total,
      pages: Math.max(1, Math.ceil(total / QA_PAGE_SIZE)),
      loading,
      /* 4 is NetworkStatus.refetch — a refresh over rows already on screen,
         which should not blank the table. */
      refreshing: networkStatus === 4,
      error,
      refetch,
    };
  }, [data, loading, error, refetch, networkStatus]);
}

/* ── scheme mapping ─────────────────────────────────────────────────────── */

/**
 * Schemes and whether each is mapped to the broker-dealer's own code.
 *
 * `isMapped: false` is the working view: an unmapped scheme reads correctly on
 * every other screen and only fails when somebody tries to place an order.
 */
export function useSchemeMapping({ isMapped = null, searchQuery = null, limit = 100, offset = 0 } = {}) {
  const { data, loading, error, refetch } = useQuery(GET_MF_SCHEMES_MAPPING, {
    variables: { limit, offset, searchQuery, isMapped },
    ...READ,
  });
  return useMemo(() => ({
    rows: data?.mfSchemes?.schemes ?? [],
    total: data?.mfSchemes?.total ?? 0,
    totalIsins: data?.mfSchemes?.totalIsins ?? 0,
    loading,
    error,
    refetch,
  }), [data, loading, error, refetch]);
}

/* ── operations ─────────────────────────────────────────────────────────── */

/** A JSON-scalar payload, which may be a bag or a bare message. */
const jsonRead = (field) => (data) => {
  const v = data?.[field];
  if (v == null) return { success: false, message: 'No response.' };
  if (typeof v === 'object') {
    return { success: v.success !== false, message: v.message || JSON.stringify(v).slice(0, 400) };
  }
  return { success: true, message: String(v) };
};
/**
 * An operation's inputs, declared so the screen can render them.
 *
 * `{ name, label, kind, default }` — kind is text, number, bool or list. A
 * required input is marked, and the run button stays off until it is given,
 * because these are expensive and a failed run costs minutes.
 */
const f = (name, label, kind = 'text', extra = {}) => ({ name, label, kind, ...extra });

/** First-run loaders. Until these have run, most of the app reads empty. */
export const BOOTSTRAP_OPS = [
  {
    key: 'goalPlanning',
    label: 'Goal planning data',
    description: 'The whole planner dataset. The first thing to run on an empty database, and what everything else here assumes.',
    doc: LOAD_GOAL_PLANNING_DATA,
    fields: [
      f('includeModels', 'Include models', 'bool', { default: true }),
      f('wait', 'Wait for it to finish', 'bool', { default: false }),
      f('force', 'Force a reload', 'bool', { default: false }),
    ],
    read: jsonRead('loadGoalPlanningData'),
  },
  {
    key: 'assetModels',
    label: 'Asset models from file',
    description: 'Loads the model definitions from a file on the server.',
    doc: LOAD_ASSET_MODELS_FROM_FILE,
    fields: [
      f('filePath', 'File path on the server', 'text', { required: true }),
      f('updateIfExists', 'Update models that already exist', 'bool', { default: true }),
    ],
    read: jsonRead('loadAssetModelsFromFile'),
  },
  {
    key: 'simGrid',
    label: 'Simulation grid',
    description: 'Rebuilds the precomputed grid the planner reads instead of simulating on demand.',
    doc: REFRESH_SIMULATION_GRID,
    read: jsonRead('refreshSimulationGrid'),
  },
];

/** Bringing the outside world in. Long, heavy, and one at a time. */
export const DATA_LOAD_OPS = [
  {
    key: 'vrFunds',
    label: 'Mutual fund master (incremental)',
    description: 'Pulls scheme data that has changed since the last run. The everyday one.',
    doc: SYNC_VR_MUTUAL_FUNDS_INCREMENTAL,
    read: jsonRead('syncVrMutualFundsIncremental'),
  },
  {
    key: 'assetModelSync',
    label: 'Asset models',
    description: 'Re-syncs every model definition.',
    doc: SYNC_ALL_ASSET_MODELS,
    fields: [f('includeEtfs', 'Include ETFs', 'bool', { default: true })],
    read: jsonRead('syncAllAssetModels'),
  },
  {
    key: 'indianDaily',
    label: 'Indian market — daily',
    description: "Today's prices and volumes. The nightly one.",
    doc: UPDATE_INDIAN_MARKET_DAILY,
    read: jsonRead('updateIndianMarketDaily'),
  },
  {
    key: 'indianComplete',
    label: 'Indian market — complete',
    description: 'The full history rather than the day. Hours, not minutes.',
    doc: UPDATE_INDIAN_MARKET_COMPLETE,
    read: jsonRead('updateIndianMarketComplete'),
  },
  {
    key: 'exchangeStocks',
    label: 'Stocks on an exchange',
    description: 'Every name on one exchange.',
    doc: UPDATE_EXCHANGE_STOCKS,
    fields: [f('exchange', 'Exchange', 'text', { required: true, default: 'NSE' })],
    read: jsonRead('updateExchangeStocks'),
  },
  {
    key: 'exchangeComplete',
    label: 'Exchange — complete',
    description: 'Prices, fundamentals and financials for every name on an exchange.',
    doc: UPDATE_EXCHANGE_COMPLETE,
    fields: [f('exchange', 'Exchange', 'text', { required: true, default: 'NSE' })],
    read: jsonRead('updateExchangeComplete'),
  },
  {
    key: 'countryStocks',
    label: 'Stocks in a country',
    description: 'Every name listed in one country.',
    doc: UPDATE_COUNTRY_STOCKS,
    fields: [f('country', 'Country', 'text', { required: true, default: 'India' })],
    read: jsonRead('updateCountryStocks'),
  },
  {
    key: 'searchIndex',
    label: 'Security search index',
    description: 'Rebuilds what the search boxes read. Run it after anything that adds securities.',
    doc: UPDATE_SECURITY_SEARCH_INDEX,
    fields: [
      f('entityType', 'Entity type (blank = all)', 'text'),
      f('limit', 'Limit', 'number'),
      f('forceRefresh', 'Force a full rebuild', 'bool', { default: false }),
    ],
    read: jsonRead('updateSecuritySearchIndex'),
  },
  {
    key: 'goldHistoricalPrices',
    label: 'Gold historical prices (SafeGold)',
    description: 'Fetches gold prices from SafeGold into the table the gold price history reads. Leave the dates blank for SafeGold\'s default range. Portfolio valuation reads daily rows only.',
    doc: SYNC_PHYSICAL_GOLD_HISTORICAL_PRICES,
    fields: [
      f('fromDate', 'From', 'date'),
      f('toDate', 'To', 'date'),
      f('intervalType', 'Interval (blank = daily)', 'select', {
        options: [['d', 'Daily'], ['w', 'Weekly'], ['m', 'Monthly'], ['q', 'Quarterly'], ['y', 'Yearly']],
      }),
    ],
    /* A failure carries its reason in `error`, not `message`. */
    read: (data) => {
      const v = data?.syncPhysicalGoldHistoricalPrices;
      if (v == null) return { success: false, message: 'No response.' };
      return v.success
        ? { success: true, message: v.message || 'Synced' }
        : { success: false, message: v.error || v.message || 'The sync failed' };
    },
  },
  {
    key: 'vrTableFromS3',
    label: 'VR table from S3',
    description: 'Loads one table of a VR bulk export from S3 into the database. Pick the export run and the table.',
    doc: LOAD_VR_TABLE_FROM_S3,
    /* The pickers list what the server has: run ids newest first, then tables. */
    fields: [
      f('runId', 'Run id', 'select', {
        required: true,
        optionsQuery: { doc: VR_BULK_RUN_IDS, read: (d) => d?.vrBulkRunIds },
      }),
      f('table', 'Table', 'select', {
        required: true,
        optionsQuery: { doc: VR_BULK_TABLES, read: (d) => d?.vrBulkTables },
      }),
    ],
    /* The message, plus what it counted and anything it skipped or failed on. */
    read: (data) => {
      const v = data?.loadVrTableFromS3;
      if (v == null) return { success: false, message: 'No response.' };
      const parts = [
        v.message,
        `${v.recordsProcessed ?? 0} records`,
        v.tablesSkipped?.length ? `skipped: ${v.tablesSkipped.join(', ')}` : null,
        v.failedSteps?.length ? `failed: ${v.failedSteps.join(', ')}` : null,
      ].filter(Boolean);
      return { success: Boolean(v.success) && !v.failedSteps?.length, message: parts.join(' · ') };
    },
  },
  {
    key: 'seedCustomerFaqs',
    label: 'Customer FAQs',
    description: 'Seeds the customer FAQs from the FAQ file. A dry run only checks the file and writes nothing; a real seed needs a super-admin sign-in. Prune also deactivates FAQs no longer in the file.',
    doc: SEED_CUSTOMER_FAQS,
    fields: [
      /* On by default: the safe choice is to check the file first. */
      f('dryRun', 'Dry run (write nothing)', 'bool', { default: true }),
      f('prune', 'Prune FAQs not in the file', 'bool', { default: false }),
    ],
    /* Writes to what customers read, so only ever run on its own. */
    noRunAll: true,
    read: (data) => {
      const v = data?.seedCustomerFaqs;
      if (v == null) return { success: false, message: 'No response.' };
      const errors = Array.isArray(v.errors) ? v.errors : (v.errors ? [v.errors] : []);
      const parts = [
        v.message,
        `${v.valid ?? 0}/${v.total ?? 0} valid`,
        `${v.inserted ?? 0} inserted, ${v.updated ?? 0} updated, ${v.deactivated ?? 0} deactivated`,
        `${v.indexEntries ?? 0} index entries`,
        errors.length ? `errors: ${errors.map((e) => (typeof e === 'string' ? e : JSON.stringify(e))).join('; ')}` : null,
      ].filter(Boolean);
      return { success: Boolean(v.success) && !errors.length, message: parts.join(' · ') };
    },
  },
  {
    key: 'comparativeExplainer',
    label: 'Comparative explainer',
    description: 'Rebuilds the asset-pair comparisons (e.g. gold vs equity). Leave the pairs blank for every curated pair, and the archetypes blank for the generic row plus every archetype.',
    doc: REFRESH_COMPARATIVE_EXPLAINER,
    /* `languages` is accepted by the backend but ignored, so it is not offered. */
    fields: [
      f('horizonYears', 'Horizon (years)', 'number', { default: 10 }),
      f('comparisonKeys', 'Pairs, comma-separated (blank = all)', 'list'),
      f('archetypes', 'Archetypes, comma-separated (blank = all)', 'list'),
    ],
    read: jsonRead('refreshComparativeExplainer'),
  },
];

/** Derived figures, rebuilt. These come AFTER a load, never before. */
export const RECALC_OPS = [
  {
    key: 'mfExposures',
    label: 'Mutual fund exposures',
    description: 'Factor exposures for schemes, from their holdings.',
    doc: UPDATE_MF_EXPOSURES,
    fields: [
      f('startDate', 'From (YYYY-MM-DD)', 'text'),
      f('endDate', 'To (YYYY-MM-DD)', 'text'),
      f('lookbackDays', 'Lookback days', 'number'),
      f('isRegular', 'Regular plans', 'bool', { default: false }),
      f('isGrowth', 'Growth plans', 'bool', { default: false }),
    ],
    read: jsonRead('updateMfExposures'),
  },
  {
    key: 'stockExposures',
    label: 'Stock exposures',
    description: 'Factor exposures for individual names.',
    doc: UPDATE_STOCK_EXPOSURES,
    fields: [
      f('startDate', 'From (YYYY-MM-DD)', 'text'),
      f('endDate', 'To (YYYY-MM-DD)', 'text'),
      f('lookbackDays', 'Lookback days', 'number'),
    ],
    read: jsonRead('updateStockExposures'),
  },
  {
    key: 'factorIndexExposures',
    label: 'Factor index exposures',
    description: 'The factor indices themselves.',
    doc: UPDATE_FACTOR_INDEX_EXPOSURES,
    fields: [f('startDate', 'From (YYYY-MM-DD)', 'text'), f('endDate', 'To (YYYY-MM-DD)', 'text')],
    read: jsonRead('updateFactorIndexExposures'),
  },
  {
    key: 'volAdjusted',
    label: 'Volatility-adjusted exposures',
    description: 'The same exposures, scaled by volatility.',
    doc: UPDATE_VOL_ADJUSTED_EXPOSURES,
    fields: [f('startDate', 'From (YYYY-MM-DD)', 'text'), f('endDate', 'To (YYYY-MM-DD)', 'text')],
    read: jsonRead('updateVolAdjustedExposures'),
  },
  {
    key: 'assetModelExposures',
    label: 'Asset model exposures',
    description: "Each model's exposures, from its constituents.",
    doc: COMPUTE_ASSET_MODEL_EXPOSURES,
    fields: [f('startDate', 'From (YYYY-MM-DD)', 'text'), f('endDate', 'To (YYYY-MM-DD)', 'text')],
    read: jsonRead('computeAssetModelExposures'),
  },
  {
    key: 'factorReturns',
    label: 'Factor portfolio returns',
    description: 'Returns for one factor portfolio.',
    doc: CALCULATE_FACTOR_PORTFOLIO_RETURNS,
    fields: [
      f('securitySymbol', 'Symbol', 'text', { required: true }),
      f('startDate', 'From (YYYY-MM-DD)', 'text'),
      f('endDate', 'To (YYYY-MM-DD)', 'text'),
    ],
    read: jsonRead('calculateFactorPortfolioReturns'),
  },
  {
    key: 'goalData',
    label: 'Goal data',
    description: 'Recomputes the planner figures for one forecast scenario. The scenario is required — there is no "whichever is active" on this call.',
    doc: RECOMPUTE_GOAL_DATA,
    fields: [
      f('scenarioId', 'Scenario id', 'number', { required: true }),
      f('includeModels', 'Include models', 'bool', { default: true }),
    ],
    /* It answers with what it wrote, not with success/message. */
    read: (data) => {
      const r = data?.recomputeGoalData;
      if (!r) return { success: false, message: 'No response.' };
      const counts = [
        r.forecastsGenerated != null && `${r.forecastsGenerated} forecasts`,
        r.simulationsGenerated != null && `${r.simulationsGenerated} simulations`,
        r.gridRows != null && `${r.gridRows} grid rows`,
        r.presetRows != null && `${r.presetRows} preset rows`,
        r.modelRows != null && `${r.modelRows} model rows`,
      ].filter(Boolean).join(' · ');
      return {
        success: !r.error,
        message: r.error || [r.status, counts].filter(Boolean).join(' — ') || 'Done.',
      };
    },
  },
];

/** Running a parametric risk simulation, and building what it needs first. */
export const RISK_RUN_OPS = [
  {
    key: 'meanCov',
    label: 'Universe mean & covariance',
    description: 'Builds the inputs a parametric simulation needs. Run this before the simulation.',
    doc: BUILD_UNIVERSE_MEAN_COVARIANCE,
    fields: [
      f('scenarioName', 'Scenario name', 'text', { required: true }),
      f('topN', 'Top N securities', 'number'),
      f('incremental', 'Incremental', 'bool', { default: true }),
      f('saveCorrelationArtifacts', 'Save correlation artifacts', 'bool', { default: false }),
    ],
    read: jsonRead('buildUniverseMeanCovariance'),
  },
  {
    key: 'parametric',
    label: 'Parametric risk simulation',
    description: 'Runs the simulation for a scenario, using the mean and covariance built above.',
    doc: TRIGGER_PARAMETRIC_RISK_SIMULATION,
    fields: [
      f('scenarioName', 'Scenario name', 'text', { required: true }),
      f('topN', 'Top N securities', 'number'),
      f('incremental', 'Incremental', 'bool', { default: true }),
      f('forceRecompute', 'Force recompute', 'bool', { default: false }),
    ],
    read: jsonRead('triggerParametricRiskSimulation'),
  },
];

/**
 * Running a declarative list of operations.
 *
 * SEQUENTIALLY, never in parallel: these rebuild overlapping data on a
 * single-worker API, and firing several at once turns a maintenance job into
 * an outage. The caller shows the progress.
 */
export function useOpRunner() {
  const client = useApolloClient();
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(null);
  const [runningAll, setRunningAll] = useState(false);

  const runOne = useCallback(async (op, variables) => {
    setBusy(op.key);
    setResults((r) => ({ ...r, [op.key]: { running: true } }));
    try {
      const { data } = await client.mutate({
        mutation: op.doc,
        variables: variables ?? op.variables,
        context: op.context,
        fetchPolicy: 'no-cache',
      });
      const read = op.read(data);
      setResults((r) => ({ ...r, [op.key]: { ...read, running: false, at: new Date().toISOString() } }));
      return read;
    } catch (err) {
      const read = { success: false, message: err.message, running: false, at: new Date().toISOString() };
      setResults((r) => ({ ...r, [op.key]: read }));
      return read;
    } finally {
      setBusy(null);
    }
  }, [client]);

  const runAll = useCallback(async (ops, varsFor) => {
    setRunningAll(true);
    /* eslint-disable no-await-in-loop -- sequential is the point; see above. */
    for (const op of ops) await runOne(op, varsFor?.(op));
    /* eslint-enable no-await-in-loop */
    setRunningAll(false);
  }, [runOne]);

  return { results, busy, runningAll, runOne, runAll };
}
